import React, { useState, useRef, useLayoutEffect, useMemo } from 'react'
import { Flex, Text, Box, Image, Collapse, Portal, PseudoBox } from '@chakra-ui/core'
import FsLightbox from 'fslightbox-react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { VideoPreview } from '../CommonComponents/VideoPreview'
import { UploadedFileList } from 'components/UploadedFileList'
import strings from 'constants/strings'
import { getFileExtension, getTextDirection } from 'utils'
import { useUserName } from 'hooks/useUserName'
import { IRefComment } from 'constants/interfaces'
import { PostMediaGrid } from 'components/NewsFeed/PostMediaGrid'
import FilePreview from 'components/CommonComponents/FilePreview'

interface IProps {
  replyMessage: IRefComment
  isUserMessage: boolean
}

export const ReplyMessage = ({ replyMessage, isUserMessage }: IProps) => {
  const { created_by, videos, images, files, message } = replyMessage

  const fullName = useUserName(created_by)

  const [isExpanded, setIsExpanded] = useState(false)
  const [showToggle, setShowToggle] = useState(false)

  const measureRef = useRef<HTMLDivElement>(null)

  const startingHeight = 20

  const allMedia = [
    ...(images?.map((img) => ({ type: 'image', src: img })) || []),
    ...(videos?.map((vid) => ({ type: 'video', src: vid })) || []),
    ...(files?.map((file) => ({ type: 'file', src: file })) || []),
  ]

  useLayoutEffect(() => {
    const el = measureRef.current
    if (el) {
      setShowToggle(el.scrollHeight > startingHeight || allMedia.length > 0)
    }
  }, [message, allMedia.length])

  const mediaTypes = useMemo(
    () =>
      allMedia
        .filter((el) => el.type !== 'file')
        .map((el) => {
          const ext = getFileExtension(el.src)
          return strings.SERVER_VIDEO_FORMATS.includes(ext) ? 'video' : 'image'
        }),
    [allMedia]
  )

  // lightbox
  const [isOpenLightBox, setIsOpenLightBox] = useState(false)
  const [currentImageIdx, setCurrentImageIdx] = useState(0)
  const openImageModalHandler = (idx: number) => {
    setCurrentImageIdx(idx)
    setIsOpenLightBox(true)
  }

  const previewItems = allMedia.slice(0, 4)
  const remainingCount = allMedia.length - 4

  return (
    <Flex
      position="relative"
      flexDir="column"
      mb="10px"
      w="100%"
      p="10px"
      borderRadius={isUserMessage ? '10px 0 10px 10px' : '0 10px 10px 10px'}
      bg="#ffffff87"
      style={{ gap: '5px' }}
      alignItems="flex-end"
    >
      <Box ref={measureRef} position="absolute" visibility="hidden" whiteSpace="normal" w="100%">
        <Text fontSize="12px" fontWeight="400">
          {message}
        </Text>
      </Box>

      <Flex
        align="center"
        flexDir={!isUserMessage ? 'row-reverse' : 'row'}
        justify={showToggle ? 'space-between' : 'flex-end'}
        w="100%"
      >
        {showToggle && (
          <PseudoBox
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            onClick={() => setIsExpanded(!isExpanded)}
            cursor="pointer"
          >
            {isExpanded ? <ChevronUp size={18} color="#2D3748" /> : <ChevronDown size={18} color="#2D3748" />}
          </PseudoBox>
        )}
        <Text fontSize="12px" fontWeight="600" color="gray.700" isTruncated maxW="80%">
          @{fullName}
        </Text>
      </Flex>
      <Box w="100%">
        <Collapse
          startingHeight="20px"
          h="100%"
          w="100%"
          isOpen={isExpanded}
          animateOpacity
          dir={getTextDirection(message)}
        >
          <Text
            fontSize="12px"
            fontWeight="400"
            color="black"
            maxW={isExpanded ? '100%' : { base: '100px', md: '350px' }}
            cursor={showToggle ? 'pointer' : 'default'}
            onClick={() => showToggle && setIsExpanded((s) => !s)}
            dir={getTextDirection(message)}
            isTruncated={!isExpanded}
          >
            {message}
          </Text>
          {allMedia.length > 0 && (
            <Flex style={{ gap: '5px' }} flexDir="column">
              {isExpanded ? (
                <>
                  {allMedia.some((item) => item.type === 'image') && (
                    <PostMediaGrid px="0" media={images} isEditable={false}>
                      {images !== null &&
                        images?.map((imageSrc, idx) => (
                          <Image
                            key={imageSrc}
                            src={imageSrc}
                            width="100%"
                            onClick={() => openImageModalHandler(idx)}
                          />
                        ))}
                    </PostMediaGrid>
                  )}
                  {allMedia.some((item) => item.type === 'video') && (
                    <PostMediaGrid px="0" media={videos} isEditable={false}>
                      {videos !== null &&
                        videos?.map((videoSrc, idx) => (
                          <VideoPreview key={videoSrc} url={videoSrc} openViewer={() => openImageModalHandler(idx)} />
                        ))}
                    </PostMediaGrid>
                  )}
                  {files && <UploadedFileList fileList={files} />}
                </>
              ) : (
                <Flex style={{ gap: '10px' }} mt="5px">
                  {previewItems.length > 0 &&
                    previewItems.map((item, index) => (
                      <Box key={index} borderRadius="10px" w="50px" h="50px" overflow="hidden" position="relative">
                        {item.type === 'image' && (
                          <Image src={item.src} alt="image" width="100%" height="100%" objectFit="cover" />
                        )}
                        {item.type === 'video' && <VideoPreview url={item.src} />}
                        {item.type === 'file' && (
                          <FilePreview
                            fileUrl={item.src}
                            width="50px"
                            height="50px"
                            textProps={{ fontSize: '6px', maxW: '50px', isTruncated: true, maxH: '40px' }}
                            iconProps={{ size: 20 }}
                          />
                        )}
                      </Box>
                    ))}
                  {remainingCount > 0 && (
                    <Flex
                      width="50px"
                      height="50px"
                      borderRadius="10px"
                      bg="gray.100"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text fontSize="sm" color="gray.500">
                        +{remainingCount}
                      </Text>
                    </Flex>
                  )}
                </Flex>
              )}
            </Flex>
          )}
        </Collapse>
      </Box>

      <Portal>
        <FsLightbox
          key={JSON.stringify(allMedia.map((el) => el.src))}
          toggler={isOpenLightBox}
          sources={allMedia.map((el) => el.src)}
          sourceIndex={currentImageIdx}
          types={mediaTypes}
        />
      </Portal>
    </Flex>
  )
}
